import { getApiBaseUrl } from "@/lib/api/api-client"

/** Resolve a relative API media path to an absolute/usable URL for <img>/<video>. */
export function resolveRosterMediaUrl(url?: string | null): string | null {
  if (!url) return null
  if (url.startsWith("http://") || url.startsWith("https://")) return url
  if (url.startsWith("/")) return `${getApiBaseUrl()}${url}`
  return url
}

/**
 * Force external profile links to be absolute so values like "test"
 * don't resolve as relative paths (e.g. /players/test).
 */
export function toExternalHref(raw?: string | null): string | null {
  const value = raw?.trim()
  if (!value) return null
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value
  if (value.startsWith("//")) return `https:${value}`
  return `https://${value.replace(/^\/+/, "")}`
}

export function rosterPlayerInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
}

export function youtubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url)
    if (u.hostname.includes("youtu.be")) {
      const id = u.pathname.replace("/", "")
      return id ? `https://www.youtube.com/embed/${id}` : null
    }
    if (u.hostname.includes("youtube.com")) {
      const id = u.searchParams.get("v")
      if (id) return `https://www.youtube.com/embed/${id}`
      const parts = u.pathname.split("/")
      const embedIdx = parts.indexOf("embed")
      if (embedIdx >= 0 && parts[embedIdx + 1]) {
        return `https://www.youtube.com/embed/${parts[embedIdx + 1]}`
      }
      const shortsIdx = parts.indexOf("shorts")
      if (shortsIdx >= 0 && parts[shortsIdx + 1]) {
        return `https://www.youtube.com/embed/${parts[shortsIdx + 1]}`
      }
    }
  } catch {
    return null
  }
  return null
}

export function vimeoEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url)
    if (!u.hostname.includes("vimeo.com")) return null
    const id = u.pathname.split("/").filter(Boolean).pop()
    return id && /^\d+$/.test(id) ? `https://player.vimeo.com/video/${id}` : null
  } catch {
    return null
  }
}

export function externalVideoEmbedUrl(url: string): string | null {
  return youtubeEmbedUrl(url) || vimeoEmbedUrl(url)
}
